import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { EmptyState } from '../common/EmptyState';
import { Select } from '../common/Select';
import { Toggle } from '../common/Toggle';
import { useToast } from '../../contexts/ToastContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { apiClient } from '../../services/api';
import type {
  PriorityLevelDto,
  PriorityMatrixEntryDto,
  SystemPriorityTemplateDto,
  UpsertSystemPriorityTemplateRequest,
} from '../../types';

const NEW_TEMPLATE_KEY = '__new__';
const PRESET_COLORS = ['#DC2626', '#EA580C', '#EAB308', '#16A34A', '#2563EB', '#9333EA', '#6B7280', '#111827'];

interface PriorityTemplateDraft {
  name: string;
  description: string;
  isDefault: boolean;
  levels: PriorityLevelDto[];
  impactLevels: PriorityLevelDto[];
  urgencyLevels: PriorityLevelDto[];
  matrix: PriorityMatrixEntryDto[];
}

function asArray<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

function createLocalId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

function createDefaultPriorityLevels(): PriorityLevelDto[] {
  return [
    { id: createLocalId('priority'), name: 'P1 - Critical', value: 1, color: '#DC2626', description: '' },
    { id: createLocalId('priority'), name: 'P2 - High', value: 2, color: '#EA580C', description: '' },
    { id: createLocalId('priority'), name: 'P3 - Medium', value: 3, color: '#EAB308', description: '' },
    { id: createLocalId('priority'), name: 'P4 - Low', value: 4, color: '#16A34A', description: '' },
  ];
}

function createDefaultImpactLevels(): PriorityLevelDto[] {
  return [
    { id: createLocalId('impact'), name: 'Critical', value: 1, color: '#DC2626', description: '' },
    { id: createLocalId('impact'), name: 'High', value: 2, color: '#EA580C', description: '' },
    { id: createLocalId('impact'), name: 'Medium', value: 3, color: '#EAB308', description: '' },
    { id: createLocalId('impact'), name: 'Low', value: 4, color: '#16A34A', description: '' },
  ];
}

function createDefaultUrgencyLevels(): PriorityLevelDto[] {
  return [
    { id: createLocalId('urgency'), name: 'Immediate', value: 1, color: '#DC2626', description: '' },
    { id: createLocalId('urgency'), name: 'Soon', value: 2, color: '#EA580C', description: '' },
    { id: createLocalId('urgency'), name: 'Normal', value: 3, color: '#EAB308', description: '' },
    { id: createLocalId('urgency'), name: 'Low', value: 4, color: '#16A34A', description: '' },
  ];
}

function toEditableLevels(levels: PriorityLevelDto[] | undefined, prefix: string): PriorityLevelDto[] {
  const safeLevels = asArray(levels).filter((level): level is PriorityLevelDto => Boolean(level && typeof level === 'object'));
  if (safeLevels.length === 0) {
    return [];
  }

  return safeLevels.map((level, index) => ({
    ...level,
    id: level.id || createLocalId(prefix),
    value: Number.isFinite(level.value) && level.value > 0 ? level.value : index + 1,
    color: level.color || PRESET_COLORS[index % PRESET_COLORS.length],
    name: level.name || `${prefix} ${index + 1}`,
  }));
}

function getMatrixPriorityId(
  entry: Partial<PriorityMatrixEntryDto>,
  levels: PriorityLevelDto[],
  fallbackId: string,
): string {
  if (entry.priorityId) {
    return entry.priorityId;
  }

  if (typeof entry.priorityLevel === 'number') {
    const mappedLevel = levels.find((level) => level.value === entry.priorityLevel);
    return mappedLevel?.id ?? fallbackId;
  }

  return fallbackId;
}

function normalizeMatrix(
  levels: PriorityLevelDto[],
  impactLevels: PriorityLevelDto[],
  urgencyLevels: PriorityLevelDto[],
  matrix: PriorityMatrixEntryDto[] | undefined,
): PriorityMatrixEntryDto[] {
  const validPriorityIds = new Set(levels.map((level) => level.id));
  const fallbackId = levels[0]?.id ?? '';
  const existing = new Map<string, PriorityMatrixEntryDto>();

  for (const entry of asArray(matrix).filter((item): item is PriorityMatrixEntryDto => Boolean(item && typeof item === 'object'))) {
    const impact = entry.impact ?? entry.impactLevel;
    const urgency = entry.urgency ?? entry.urgencyLevel;
    if (typeof impact === 'number' && typeof urgency === 'number') {
      existing.set(`${impact}|${urgency}`, entry);
    }
  }

  const sortedImpactLevels = [...impactLevels].sort((a, b) => a.value - b.value);
  const sortedUrgencyLevels = [...urgencyLevels].sort((a, b) => a.value - b.value);

  return sortedImpactLevels.flatMap((impactLevel) =>
    sortedUrgencyLevels.map((urgencyLevel) => {
      const key = `${impactLevel.value}|${urgencyLevel.value}`;
      const previous = existing.get(key);
      const priorityId = previous ? getMatrixPriorityId(previous, levels, fallbackId) : fallbackId;

      return {
        impact: impactLevel.value,
        urgency: urgencyLevel.value,
        priorityId: validPriorityIds.has(priorityId) ? priorityId : fallbackId,
      };
    }),
  );
}

function createDefaultMatrix(
  levels: PriorityLevelDto[],
  impactLevels: PriorityLevelDto[],
  urgencyLevels: PriorityLevelDto[],
): PriorityMatrixEntryDto[] {
  const sortedLevels = [...levels].sort((a, b) => a.value - b.value);
  const sortedImpactLevels = [...impactLevels].sort((a, b) => a.value - b.value);
  const sortedUrgencyLevels = [...urgencyLevels].sort((a, b) => a.value - b.value);

  return sortedImpactLevels.flatMap((impactLevel, impactIndex) =>
    sortedUrgencyLevels.map((urgencyLevel, urgencyIndex) => {
      const weightedSeverity = impactIndex + urgencyIndex;
      const maxSeverity = Math.max(sortedImpactLevels.length + sortedUrgencyLevels.length - 2, 1);
      const levelIndex = Math.min(
        sortedLevels.length - 1,
        Math.round((weightedSeverity / maxSeverity) * Math.max(sortedLevels.length - 1, 0)),
      );
      const level = sortedLevels[levelIndex] ?? sortedLevels[0];

      return {
        impact: impactLevel.value,
        urgency: urgencyLevel.value,
        priorityId: level?.id ?? '',
      };
    }),
  );
}

function createDefaultDraft(): PriorityTemplateDraft {
  const levels = createDefaultPriorityLevels();
  const impactLevels = createDefaultImpactLevels();
  const urgencyLevels = createDefaultUrgencyLevels();

  return {
    name: '',
    description: '',
    isDefault: false,
    levels,
    impactLevels,
    urgencyLevels,
    matrix: createDefaultMatrix(levels, impactLevels, urgencyLevels),
  };
}

function toDraft(template: SystemPriorityTemplateDto): PriorityTemplateDraft {
  const levels = toEditableLevels(template.priorityLevels ?? template.levels, 'priority');
  const normalizedLevels = levels.length > 0 ? levels : createDefaultPriorityLevels();
  const impactLevels = toEditableLevels(template.impactLevels, 'impact');
  const urgencyLevels = toEditableLevels(template.urgencyLevels, 'urgency');

  const normalizedImpactLevels = impactLevels.length > 0 ? impactLevels : createDefaultImpactLevels();
  const normalizedUrgencyLevels = urgencyLevels.length > 0 ? urgencyLevels : createDefaultUrgencyLevels();
  const matrix = asArray(template.matrix);
  const priorityMatrix = asArray(template.priorityMatrix);
  const sourceMatrix = matrix.length > 0 ? matrix : priorityMatrix;
  const normalizedMatrix =
    sourceMatrix.length > 0
      ? normalizeMatrix(normalizedLevels, normalizedImpactLevels, normalizedUrgencyLevels, sourceMatrix)
      : createDefaultMatrix(normalizedLevels, normalizedImpactLevels, normalizedUrgencyLevels);

  return {
    name: template.name ?? '',
    description: template.description ?? '',
    isDefault: Boolean(template.isDefault),
    levels: normalizedLevels,
    impactLevels: normalizedImpactLevels,
    urgencyLevels: normalizedUrgencyLevels,
    matrix: normalizedMatrix,
  };
}

function stripLevelForPayload(level: PriorityLevelDto): Omit<PriorityLevelDto, 'id'> {
  return {
    name: level.name.trim(),
    value: level.value,
    color: level.color,
    description: level.description?.trim() || undefined,
  };
}

function toNumericPriorityMatrix(
  matrix: PriorityMatrixEntryDto[] | undefined,
  levels: PriorityLevelDto[],
): PriorityMatrixEntryDto[] {
  const valueByPriorityId = new Map(levels.map((level) => [level.id, level.value]));
  const fallbackValue = levels[0]?.value ?? 1;

  return asArray(matrix).map((entry) => ({
    impact: entry.impact,
    urgency: entry.urgency,
    priorityId: entry.priorityId,
    impactLevel: entry.impact,
    urgencyLevel: entry.urgency,
    priorityLevel: valueByPriorityId.get(entry.priorityId) ?? fallbackValue,
  }));
}

function normalizeTemplate(rawTemplate: SystemPriorityTemplateDto, index: number): SystemPriorityTemplateDto {
  const safeTemplate = rawTemplate ?? ({} as SystemPriorityTemplateDto);
  return {
    ...safeTemplate,
    id: typeof safeTemplate.id === 'string' && safeTemplate.id.trim() ? safeTemplate.id : `priority-template-${index + 1}`,
    name:
      typeof safeTemplate.name === 'string' && safeTemplate.name.trim()
        ? safeTemplate.name
        : `Priority Template ${index + 1}`,
    description: typeof safeTemplate.description === 'string' ? safeTemplate.description : '',
    isDefault: Boolean(safeTemplate.isDefault),
    levels: toEditableLevels(asArray(safeTemplate.levels), 'priority'),
    priorityLevels: toEditableLevels(asArray(safeTemplate.priorityLevels), 'priority'),
    impactLevels: toEditableLevels(asArray(safeTemplate.impactLevels), 'impact'),
    urgencyLevels: toEditableLevels(asArray(safeTemplate.urgencyLevels), 'urgency'),
    matrix: asArray(safeTemplate.matrix),
    priorityMatrix: asArray(safeTemplate.priorityMatrix),
    createdAt: typeof safeTemplate.createdAt === 'string' ? safeTemplate.createdAt : '',
    updatedAt: typeof safeTemplate.updatedAt === 'string' ? safeTemplate.updatedAt : undefined,
  };
}

export function PriorityMatrixTab() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string | null>(null);
  const [newTemplateDraft, setNewTemplateDraft] = useState<PriorityTemplateDraft | null>(null);
  const [draftsByTemplateId, setDraftsByTemplateId] = useState<Record<string, PriorityTemplateDraft>>({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEscapeKey(() => setShowDeleteDialog(false), showDeleteDialog);

  const templatesQuery = useQuery<SystemPriorityTemplateDto[]>({
    queryKey: ['itil', 'priority-templates'],
    queryFn: async () => {
      try {
        const response = await apiClient.getSystemPriorityTemplates();
        if (!Array.isArray(response)) {
          throw new Error('Priority templates response is not an array.');
        }
        return response.map((template, index) => normalizeTemplate(template, index));
      } catch (error) {
        throw new Error(errorMessage(error, 'Failed to load priority templates.'));
      }
    },
  });

  const templates = Array.isArray(templatesQuery.data) ? templatesQuery.data : [];
  const requestedTemplateId =
    selectedTemplateKey && selectedTemplateKey !== NEW_TEMPLATE_KEY ? selectedTemplateKey : null;
  const selectedExistingTemplateId =
    requestedTemplateId && templates.some((template) => template.id === requestedTemplateId)
      ? requestedTemplateId
      : templates[0]?.id ?? null;
  const selectedTemplate = selectedExistingTemplateId
    ? templates.find((template) => template.id === selectedExistingTemplateId) ?? null
    : null;
  const isEditingNew = selectedTemplateKey === NEW_TEMPLATE_KEY;

  const activeDraft = useMemo(() => {
    if (isEditingNew) {
      return newTemplateDraft;
    }

    if (!selectedTemplate) {
      return null;
    }

    return draftsByTemplateId[selectedTemplate.id] ?? toDraft(selectedTemplate);
  }, [draftsByTemplateId, isEditingNew, newTemplateDraft, selectedTemplate]);

  const upsertMutation = useMutation({
    mutationFn: async (draft: PriorityTemplateDraft) => {
      try {
        const normalizedMatrix = normalizeMatrix(draft.levels, draft.impactLevels, draft.urgencyLevels, draft.matrix);
        const priorityLevelsPayload = draft.levels.map(stripLevelForPayload);
        const impactLevelsPayload = draft.impactLevels.map(stripLevelForPayload);
        const urgencyLevelsPayload = draft.urgencyLevels.map(stripLevelForPayload);

        const payload: UpsertSystemPriorityTemplateRequest = {
          name: draft.name.trim(),
          description: draft.description.trim() || undefined,
          isDefault: draft.isDefault,
          levels: priorityLevelsPayload,
          matrix: normalizedMatrix,
          priorityLevels: priorityLevelsPayload,
          impactLevels: impactLevelsPayload,
          urgencyLevels: urgencyLevelsPayload,
          priorityMatrix: toNumericPriorityMatrix(normalizedMatrix, draft.levels),
        };

        const templateId = isEditingNew ? undefined : selectedTemplate?.id;
        return await apiClient.upsertSystemPriorityTemplate(payload, templateId);
      } catch (error) {
        throw new Error(errorMessage(error, 'Failed to save priority template.'));
      }
    },
    onSuccess: async (savedTemplate) => {
      await queryClient.invalidateQueries({ queryKey: ['itil', 'priority-templates'] });
      addToast('Priority template saved successfully.', 'success');

      if (isEditingNew) {
        setNewTemplateDraft(null);
      }

      const savedTemplateId = savedTemplate?.id ?? null;
      setSelectedTemplateKey(savedTemplateId);
      setDraftsByTemplateId((current) => {
        if (!savedTemplateId) {
          return current;
        }
        const next = { ...current };
        delete next[savedTemplateId];
        return next;
      });
    },
    onError: (error) => {
      addToast(errorMessage(error, 'Failed to save priority template.'), 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (templateId: string) => {
      try {
        await apiClient.deleteSystemPriorityTemplate(templateId);
      } catch (error) {
        throw new Error(errorMessage(error, 'Failed to delete priority template.'));
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['itil', 'priority-templates'] });
      addToast('Priority template deleted.', 'success');
      setShowDeleteDialog(false);
      setSelectedTemplateKey(null);
      setDraftsByTemplateId({});
    },
    onError: (error) => {
      addToast(errorMessage(error, 'Failed to delete priority template.'), 'error');
    },
  });

  const templateOptions = templates.map((template) => ({
    value: template.id,
    label: template.isDefault ? `${template.name} (Default)` : template.name,
  }));

  if (newTemplateDraft) {
    templateOptions.push({
      value: NEW_TEMPLATE_KEY,
      label: newTemplateDraft.name.trim() || 'New template (unsaved)',
    });
  }

  const updateActiveDraft = (updater: (draft: PriorityTemplateDraft) => PriorityTemplateDraft) => {
    if (isEditingNew) {
      setNewTemplateDraft((current) => (current ? updater(current) : current));
      return;
    }

    if (!selectedTemplate) {
      return;
    }

    setDraftsByTemplateId((current) => ({
      ...current,
      [selectedTemplate.id]: updater(current[selectedTemplate.id] ?? toDraft(selectedTemplate)),
    }));
  };

  const updateMatrixCell = (impact: number, urgency: number, priorityId: string) => {
    updateActiveDraft((draft) => {
      const nextMatrix = [...draft.matrix];
      const existingIndex = nextMatrix.findIndex((entry) => entry.impact === impact && entry.urgency === urgency);

      if (existingIndex >= 0) {
        nextMatrix[existingIndex] = { ...nextMatrix[existingIndex], priorityId };
      } else {
        nextMatrix.push({ impact, urgency, priorityId });
      }

      return { ...draft, matrix: nextMatrix };
    });
  };

  const handleCreateTemplate = () => {
    setNewTemplateDraft(createDefaultDraft());
    setSelectedTemplateKey(NEW_TEMPLATE_KEY);
  };

  const addPriorityLevel = () => {
    updateActiveDraft((draft) => {
      const nextValue = Math.max(0, ...draft.levels.map((level) => level.value)) + 1;
      const nextLevels = [
        ...draft.levels,
        {
          id: createLocalId('priority'),
          name: `P${nextValue}`,
          value: nextValue,
          color: PRESET_COLORS[draft.levels.length % PRESET_COLORS.length],
          description: '',
        },
      ];

      return {
        ...draft,
        levels: nextLevels,
        matrix: normalizeMatrix(nextLevels, draft.impactLevels, draft.urgencyLevels, draft.matrix),
      };
    });
  };

  const removePriorityLevel = (levelId: string) => {
    updateActiveDraft((draft) => {
      if (draft.levels.length <= 1) {
        return draft;
      }

      const nextLevels = draft.levels.filter((level) => level.id !== levelId);
      return {
        ...draft,
        levels: nextLevels,
        matrix: normalizeMatrix(nextLevels, draft.impactLevels, draft.urgencyLevels, draft.matrix),
      };
    });
  };

  const addImpactLevel = () => {
    updateActiveDraft((draft) => {
      const nextValue = Math.max(0, ...draft.impactLevels.map((level) => level.value)) + 1;
      const nextImpactLevels = [
        ...draft.impactLevels,
        {
          id: createLocalId('impact'),
          name: `Impact ${nextValue}`,
          value: nextValue,
          color: PRESET_COLORS[draft.impactLevels.length % PRESET_COLORS.length],
          description: '',
        },
      ];

      return {
        ...draft,
        impactLevels: nextImpactLevels,
        matrix: normalizeMatrix(draft.levels, nextImpactLevels, draft.urgencyLevels, draft.matrix),
      };
    });
  };

  const removeImpactLevel = (levelId: string) => {
    updateActiveDraft((draft) => {
      if (draft.impactLevels.length <= 1) {
        return draft;
      }

      const nextImpactLevels = draft.impactLevels.filter((level) => level.id !== levelId);
      return {
        ...draft,
        impactLevels: nextImpactLevels,
        matrix: normalizeMatrix(draft.levels, nextImpactLevels, draft.urgencyLevels, draft.matrix),
      };
    });
  };

  const addUrgencyLevel = () => {
    updateActiveDraft((draft) => {
      const nextValue = Math.max(0, ...draft.urgencyLevels.map((level) => level.value)) + 1;
      const nextUrgencyLevels = [
        ...draft.urgencyLevels,
        {
          id: createLocalId('urgency'),
          name: `Urgency ${nextValue}`,
          value: nextValue,
          color: PRESET_COLORS[draft.urgencyLevels.length % PRESET_COLORS.length],
          description: '',
        },
      ];

      return {
        ...draft,
        urgencyLevels: nextUrgencyLevels,
        matrix: normalizeMatrix(draft.levels, draft.impactLevels, nextUrgencyLevels, draft.matrix),
      };
    });
  };

  const removeUrgencyLevel = (levelId: string) => {
    updateActiveDraft((draft) => {
      if (draft.urgencyLevels.length <= 1) {
        return draft;
      }

      const nextUrgencyLevels = draft.urgencyLevels.filter((level) => level.id !== levelId);
      return {
        ...draft,
        urgencyLevels: nextUrgencyLevels,
        matrix: normalizeMatrix(draft.levels, draft.impactLevels, nextUrgencyLevels, draft.matrix),
      };
    });
  };

  const validateDraft = (draft: PriorityTemplateDraft): string | null => {
    const name = draft.name.trim();
    if (!name) {
      return 'Template name is required.';
    }

    const hasDuplicateTemplateName = templates.some(
      (template) =>
        template.name.trim().toLowerCase() === name.toLowerCase() &&
        (isEditingNew || template.id !== selectedTemplate?.id),
    );
    if (hasDuplicateTemplateName) {
      return 'Template name must be unique.';
    }

    if (draft.levels.length === 0 || draft.impactLevels.length === 0 || draft.urgencyLevels.length === 0) {
      return 'Priority, impact, and urgency levels are all required.';
    }

    const priorityNames = new Set<string>();
    const priorityValues = new Set<number>();
    for (const level of draft.levels) {
      const levelName = level.name.trim().toLowerCase();
      if (!levelName) {
        return 'Each priority level requires a name.';
      }
      if (priorityNames.has(levelName)) {
        return 'Priority level names must be unique.';
      }
      priorityNames.add(levelName);

      if (!Number.isFinite(level.value) || level.value < 1) {
        return 'Priority values must be positive numbers.';
      }
      if (priorityValues.has(level.value)) {
        return 'Priority values must be unique.';
      }
      priorityValues.add(level.value);
    }

    for (const impactLevel of draft.impactLevels) {
      if (!impactLevel.name.trim()) {
        return 'Each impact level requires a name.';
      }
    }

    for (const urgencyLevel of draft.urgencyLevels) {
      if (!urgencyLevel.name.trim()) {
        return 'Each urgency level requires a name.';
      }
    }

    return null;
  };

  const handleSave = () => {
    if (!activeDraft) {
      return;
    }

    const validationError = validateDraft(activeDraft);
    if (validationError) {
      addToast(validationError, 'error');
      return;
    }

    upsertMutation.mutate(activeDraft);
  };

  const isLoading = templatesQuery.isLoading;
  const isError = templatesQuery.isError;
  const selectedValue = isEditingNew ? NEW_TEMPLATE_KEY : selectedTemplate?.id ?? '';

  const sortedPriorityLevels = useMemo(
    () => [...(activeDraft?.levels ?? [])].sort((a, b) => a.value - b.value),
    [activeDraft?.levels],
  );
  const sortedImpactLevels = useMemo(
    () => [...(activeDraft?.impactLevels ?? [])].sort((a, b) => a.value - b.value),
    [activeDraft?.impactLevels],
  );
  const sortedUrgencyLevels = useMemo(
    () => [...(activeDraft?.urgencyLevels ?? [])].sort((a, b) => a.value - b.value),
    [activeDraft?.urgencyLevels],
  );

  if (isLoading) {
    return (
      <div className="card">
        <p className="text-sm text-gray-600 dark:text-gray-300">Loading priority templates…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="card rounded border border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
        Unable to load priority templates. {errorMessage(templatesQuery.error, '')}
      </div>
    );
  }

  if (!activeDraft && templates.length === 0) {
    return (
      <EmptyState
        title="No priority templates"
        message="Create your first priority matrix template."
        action={
          <button
            type="button"
            onClick={handleCreateTemplate}
            className="rounded-md bg-primary-700 px-4 py-2 text-sm font-medium text-white hover:bg-primary-800 dark:bg-primary-600 dark:hover:bg-primary-700"
          >
            Create New Template
          </button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <section className="card space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[280px] flex-1">
            <Select
              id="priority-template-select"
              label="Template"
              value={selectedValue}
              onChange={setSelectedTemplateKey}
              options={templateOptions}
            />
          </div>
          <button
            type="button"
            onClick={handleCreateTemplate}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"
          >
            Create New Template
          </button>
        </div>
      </section>

      {activeDraft ? (
        <section className="card space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="priority-template-name">
                Name
              </label>
              <input
                id="priority-template-name"
                type="text"
                value={activeDraft.name}
                onChange={(event) => updateActiveDraft((draft) => ({ ...draft, name: event.target.value }))}
                className="input"
              />
            </div>
            <div className="flex items-end">
              <Toggle
                id="priority-template-default"
                label="Set as Default"
                checked={activeDraft.isDefault}
                onChange={(checked) => updateActiveDraft((draft) => ({ ...draft, isDefault: checked }))}
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="priority-template-description">
              Description
            </label>
            <textarea
              id="priority-template-description"
              value={activeDraft.description}
              onChange={(event) => updateActiveDraft((draft) => ({ ...draft, description: event.target.value }))}
              className="input min-h-[88px]"
            />
          </div>

          <section className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Priority Levels</h3>
              <button
                type="button"
                onClick={addPriorityLevel}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
              >
                Add Priority Level
              </button>
            </div>
            <div className="space-y-3">
              {sortedPriorityLevels.map((level) => (
                <div key={level.id} className="grid gap-3 rounded-lg border border-gray-200 p-3 md:grid-cols-12 dark:border-gray-700">
                  <div className="md:col-span-5">
                    <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Name</label>
                    <input
                      type="text"
                      value={level.name}
                      onChange={(event) =>
                        updateActiveDraft((draft) => ({
                          ...draft,
                          levels: draft.levels.map((item) => (item.id === level.id ? { ...item, name: event.target.value } : item)),
                        }))
                      }
                      className="input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Value</label>
                    <input
                      type="number"
                      min={1}
                      value={level.value}
                      onChange={(event) =>
                        updateActiveDraft((draft) => ({
                          ...draft,
                          levels: draft.levels.map((item) =>
                            item.id === level.id ? { ...item, value: Number.parseInt(event.target.value, 10) || 1 } : item,
                          ),
                        }))
                      }
                      className="input"
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Color</label>
                    <input
                      type="color"
                      value={level.color}
                      onChange={(event) =>
                        updateActiveDraft((draft) => ({
                          ...draft,
                          levels: draft.levels.map((item) => (item.id === level.id ? { ...item, color: event.target.value } : item)),
                        }))
                      }
                      className="h-10 w-full rounded border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-900"
                      aria-label={`Color for ${level.name}`}
                    />
                  </div>
                  <div className="flex items-end justify-end md:col-span-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (level.id) {
                          removePriorityLevel(level.id);
                        }
                      }}
                      disabled={activeDraft.levels.length <= 1 || !level.id}
                      className="rounded-md border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/20"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div className="grid gap-4 lg:grid-cols-2">
            <section className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Impact Levels</h3>
                <button
                  type="button"
                  onClick={addImpactLevel}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
                >
                  Add Impact Level
                </button>
              </div>
              <div className="space-y-2">
                {sortedImpactLevels.map((level) => (
                  <div key={level.id} className="grid grid-cols-[1fr_auto] items-center gap-2 rounded border border-gray-200 p-2 dark:border-gray-700">
                    <input
                      type="text"
                      value={level.name}
                      onChange={(event) =>
                        updateActiveDraft((draft) => ({
                          ...draft,
                          impactLevels: draft.impactLevels.map((item) =>
                            item.id === level.id ? { ...item, name: event.target.value } : item,
                          ),
                        }))
                      }
                      className="input"
                      aria-label={`Impact level ${level.value} name`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (level.id) {
                          removeImpactLevel(level.id);
                        }
                      }}
                      disabled={activeDraft.impactLevels.length <= 1 || !level.id}
                      className="rounded-md border border-red-300 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/20"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </section>

            <section className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Urgency Levels</h3>
                <button
                  type="button"
                  onClick={addUrgencyLevel}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
                >
                  Add Urgency Level
                </button>
              </div>
              <div className="space-y-2">
                {sortedUrgencyLevels.map((level) => (
                  <div key={level.id} className="grid grid-cols-[1fr_auto] items-center gap-2 rounded border border-gray-200 p-2 dark:border-gray-700">
                    <input
                      type="text"
                      value={level.name}
                      onChange={(event) =>
                        updateActiveDraft((draft) => ({
                          ...draft,
                          urgencyLevels: draft.urgencyLevels.map((item) =>
                            item.id === level.id ? { ...item, name: event.target.value } : item,
                          ),
                        }))
                      }
                      className="input"
                      aria-label={`Urgency level ${level.value} name`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (level.id) {
                          removeUrgencyLevel(level.id);
                        }
                      }}
                      disabled={activeDraft.urgencyLevels.length <= 1 || !level.id}
                      className="rounded-md border border-red-300 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/20"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <section className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Matrix Editor</h3>
            {sortedPriorityLevels.length === 0 || sortedImpactLevels.length === 0 || sortedUrgencyLevels.length === 0 ? (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Add at least one priority, impact, and urgency level to edit the matrix.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse text-sm">
                  <caption className="sr-only">Impact by urgency priority matrix editor</caption>
                  <thead>
                    <tr>
                      <th className="border border-gray-200 px-3 py-2 text-left font-semibold dark:border-gray-700">
                        Impact \ Urgency
                      </th>
                      {sortedUrgencyLevels.map((urgencyLevel) => (
                        <th
                          key={urgencyLevel.id}
                          className="border border-gray-200 px-2 py-2 text-left font-semibold dark:border-gray-700"
                          scope="col"
                        >
                          {urgencyLevel.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedImpactLevels.map((impactLevel) => (
                      <tr key={impactLevel.id}>
                        <th className="border border-gray-200 px-3 py-2 text-left font-semibold dark:border-gray-700" scope="row">
                          {impactLevel.name}
                        </th>
                        {sortedUrgencyLevels.map((urgencyLevel) => {
                          const currentEntry = activeDraft.matrix.find(
                            (entry) => entry.impact === impactLevel.value && entry.urgency === urgencyLevel.value,
                          );
                          const selectedPriorityId = currentEntry?.priorityId ?? sortedPriorityLevels[0].id;
                          const selectedPriority = sortedPriorityLevels.find((level) => level.id === selectedPriorityId);

                          return (
                            <td key={`${impactLevel.id}-${urgencyLevel.id}`} className="border border-gray-200 p-1 dark:border-gray-700">
                              <select
                                value={selectedPriorityId}
                                onChange={(event) => updateMatrixCell(impactLevel.value, urgencyLevel.value, event.target.value)}
                                className="w-full rounded border border-gray-300 px-2 py-2 text-xs font-medium text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-600"
                                style={{ backgroundColor: selectedPriority?.color ?? '#4B5563' }}
                                aria-label={`Priority for impact ${impactLevel.name} and urgency ${urgencyLevel.name}`}
                              >
                                {sortedPriorityLevels.map((priorityLevel) => (
                                  <option key={priorityLevel.id} value={priorityLevel.id}>
                                    {priorityLevel.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={upsertMutation.isPending}
              className="rounded-md bg-primary-700 px-4 py-2 text-sm font-medium text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-primary-600 dark:hover:bg-primary-700"
            >
              {upsertMutation.isPending ? 'Saving…' : 'Save'}
            </button>
            {!isEditingNew && selectedTemplate ? (
              <button
                type="button"
                onClick={() => setShowDeleteDialog(true)}
                className="rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/20"
              >
                Delete
              </button>
            ) : null}
          </div>
        </section>
      ) : null}

      <ConfirmDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={() => {
          if (selectedTemplate) {
            deleteMutation.mutate(selectedTemplate.id);
          }
        }}
        title="Delete template?"
        message="This will permanently remove the selected priority template."
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
